import Button, { ButtonVariant } from "@shared/components/ui/Button";
import theme from "@shared/utils/themeColors";
import EmojiPicker from "emoji-picker-react";
import React, { useEffect, useState } from "react";
import {
  CalendarEvent,
  CreateEventData,
  EventCategory,
  UpdateEventData,
} from "../types/calendar.types";

interface EventFormProps {
  event?: CalendarEvent;
  categories: EventCategory[];
  onSubmit: (data: CreateEventData | UpdateEventData) => Promise<void>;
  onDelete?: () => Promise<void>;
  onCancel: () => void;
  isSubmitting: boolean;
}

const EventForm: React.FC<EventFormProps> = ({
  event,
  categories,
  onSubmit,
  onDelete,
  onCancel,
  isSubmitting,
}) => {
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [startDate, setStartDate] = useState("");
  const [startTime, setStartTime] = useState("");
  const [endDate, setEndDate] = useState("");
  const [endTime, setEndTime] = useState("");
  const [categoryId, setCategoryId] = useState("");
  const [url, setUrl] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [emojiTarget, setEmojiTarget] = useState<"title" | "description">(
    "title"
  );

  // Initialize form with event data if editing
  useEffect(() => {
    if (event) {
      setTitle(event.title);
      setDescription(event.description || "");

      const start = event.startDate.toDate();
      setStartDate(formatDateForInput(start));
      setStartTime(formatTimeForInput(start));

      if (event.endDate) {
        const end = event.endDate.toDate();
        setEndDate(formatDateForInput(end));
        setEndTime(formatTimeForInput(end));
      }

      setCategoryId(event.categoryId);
      setUrl(event.url || "");
    } else if (categories.length > 0) {
      // Set default category for new events
      setCategoryId(categories[0].id);
    }
  }, [event, categories]);

  const formatDateForInput = (date: Date): string => {
    return date.toISOString().split("T")[0];
  };

  const formatTimeForInput = (date: Date): string => {
    return date.toTimeString().slice(0, 5);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validate form
    if (!title.trim()) {
      setError("Event title is required");
      return;
    }

    if (!startDate || !startTime) {
      setError("Start date and time are required");
      return;
    }

    if (!categoryId) {
      setError("Please select a category");
      return;
    }

    try {
      const startDateTime = new Date(`${startDate}T${startTime}`);
      let endDateTime: Date | undefined;

      if (endDate && endTime) {
        endDateTime = new Date(`${endDate}T${endTime}`);

        if (endDateTime < startDateTime) {
          setError("End date/time cannot be before start date/time");
          return;
        }
      }

      const eventData: CreateEventData | UpdateEventData = {
        title,
        description: description || undefined,
        startDate: startDateTime,
        endDate: endDateTime,
        categoryId,
        url: url || undefined,
      };

      await onSubmit(eventData);
      onCancel(); // Close the modal on success
    } catch (err) {
      console.error("Error submitting event form:", err);
      setError("Failed to save event. Please try again.");
    }
  };

  const handleDelete = async () => {
    if (!onDelete) return;

    try {
      await onDelete();
      onCancel(); // Close the modal on success
    } catch (err) {
      console.error("Error deleting event:", err);
      setError("Failed to delete event. Please try again.");
    }
  };

  const handleEmojiClick = (emojiData: { emoji: string }) => {
    if (emojiTarget === "title") {
      setTitle((prev) => prev + emojiData.emoji);
    } else {
      setDescription((prev) => prev + emojiData.emoji);
    }
    setShowEmojiPicker(false);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="relative">
        <label
          htmlFor="event-title"
          className={`block text-sm font-medium ${theme.text.secondary} mb-1`}
        >
          Event Title
        </label>
        <div className="flex">
          <input
            id="event-title"
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            className={`form-input w-full ${theme.bg.inactive}`}
            placeholder="Enter event title"
            disabled={isSubmitting}
          />
          <button
            type="button"
            className="ml-2 p-2 rounded-md text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300"
            onClick={() => {
              setEmojiTarget("title");
              setShowEmojiPicker(!showEmojiPicker);
            }}
          >
            <svg
              className={`${theme.bg.fill}`}
              width="20"
              height="20"
              viewBox="0 0 16 16"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path d="M9.793 10.002a.5.5 0 0 1 .353.853l-1.792 1.793a.5.5 0 0 1-.708 0l-1.792-1.793a.5.5 0 0 1 .353-.853h3.586Zm5.014-4.63c1.178 2.497 1.833 5.647.258 7.928-1.238 1.793-3.615 2.702-7.065 2.702S2.173 15.092.935 13.3c-1.575-2.28-.92-5.431.258-7.927A2.962 2.962 0 0 1 0 3.002a3 3 0 0 1 3-3c.787 0 1.496.309 2.029.806a5.866 5.866 0 0 1 5.942 0A2.96 2.96 0 0 1 13 .002a3 3 0 0 1 3 3c0 .974-.472 1.827-1.193 2.37Zm-1.387 6.79c1.05-1.522.417-3.835-.055-5.078C12.915 5.89 11.192 2.002 8 2.002s-4.914 3.89-5.365 5.082c-.472 1.243-1.106 3.556-.055 5.079.843 1.22 2.666 1.839 5.42 1.839s4.577-.62 5.42-1.84ZM6.67 6.62c.113.443.102.68-.433 1.442-.535.761-1.06 1.297-1.658 1.297-.597 0-1.08-.772-1.07-1.483.01-.71.916-2.306 1.997-2.306.784 0 1.05.607 1.163 1.05Zm3.824-1.05c1.08 0 1.987 1.596 1.997 2.306.01.71-.473 1.483-1.07 1.483-.598 0-1.123-.536-1.658-1.297-.535-.762-.546-1-.432-1.442.113-.443.38-1.05 1.163-1.05Z" />
            </svg>
          </button>
        </div>
      </div>

      <div className="relative">
        <label
          htmlFor="event-description"
          className={`block text-sm font-medium ${theme.text.secondary} mb-1`}
        >
          Description
        </label>
        <div className="flex">
          <textarea
            id="event-description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            className={`form-textarea w-full ${theme.bg.inactive}`}
            rows={3}
            placeholder="Enter event description"
            disabled={isSubmitting}
          />
          <button
            type="button"
            className="ml-2 p-2 rounded-md text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300"
            onClick={() => {
              setEmojiTarget("description");
              setShowEmojiPicker(!showEmojiPicker);
            }}
          >
            <svg
              className={`${theme.bg.fill}`}
              width="20"
              height="20"
              viewBox="0 0 16 16"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path d="M9.793 10.002a.5.5 0 0 1 .353.853l-1.792 1.793a.5.5 0 0 1-.708 0l-1.792-1.793a.5.5 0 0 1 .353-.853h3.586Zm5.014-4.63c1.178 2.497 1.833 5.647.258 7.928-1.238 1.793-3.615 2.702-7.065 2.702S2.173 15.092.935 13.3c-1.575-2.28-.92-5.431.258-7.927A2.962 2.962 0 0 1 0 3.002a3 3 0 0 1 3-3c.787 0 1.496.309 2.029.806a5.866 5.866 0 0 1 5.942 0A2.96 2.96 0 0 1 13 .002a3 3 0 0 1 3 3c0 .974-.472 1.827-1.193 2.37Zm-1.387 6.79c1.05-1.522.417-3.835-.055-5.078C12.915 5.89 11.192 2.002 8 2.002s-4.914 3.89-5.365 5.082c-.472 1.243-1.106 3.556-.055 5.079.843 1.22 2.666 1.839 5.42 1.839s4.577-.62 5.42-1.84ZM6.67 6.62c.113.443.102.68-.433 1.442-.535.761-1.06 1.297-1.658 1.297-.597 0-1.08-.772-1.07-1.483.01-.71.916-2.306 1.997-2.306.784 0 1.05.607 1.163 1.05Zm3.824-1.05c1.08 0 1.987 1.596 1.997 2.306.01.71-.473 1.483-1.07 1.483-.598 0-1.123-.536-1.658-1.297-.535-.762-.546-1-.432-1.442.113-.443.38-1.05 1.163-1.05Z" />
            </svg>
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label
            htmlFor="start-date"
            className={`block text-sm font-medium ${theme.text.secondary} mb-1`}
          >
            Start Date
          </label>
          <input
            id="start-date"
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            className={`form-input w-full ${theme.bg.inactive}`}
            disabled={isSubmitting}
          />
        </div>
        <div>
          <label
            htmlFor="start-time"
            className={`block text-sm font-medium ${theme.text.secondary} mb-1`}
          >
            Start Time
          </label>
          <input
            id="start-time"
            type="time"
            value={startTime}
            onChange={(e) => setStartTime(e.target.value)}
            className={`form-input w-full ${theme.bg.inactive}`}
            disabled={isSubmitting}
          />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label
            htmlFor="end-date"
            className={`block text-sm font-medium ${theme.text.secondary} mb-1`}
          >
            End Date (Optional)
          </label>
          <input
            id="end-date"
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            className={`form-input w-full ${theme.bg.inactive}`}
            disabled={isSubmitting}
          />
        </div>
        <div>
          <label
            htmlFor="end-time"
            className={`block text-sm font-medium ${theme.text.secondary} mb-1`}
          >
            End Time (Optional)
          </label>
          <input
            id="end-time"
            type="time"
            value={endTime}
            onChange={(e) => setEndTime(e.target.value)}
            className={`form-input w-full ${theme.bg.inactive}`}
            disabled={isSubmitting}
          />
        </div>
      </div>

      <div>
        <label
          htmlFor="category"
          className={`block text-sm font-medium ${theme.text.secondary} mb-1`}
        >
          Category
        </label>
        <select
          id="category"
          value={categoryId}
          onChange={(e) => setCategoryId(e.target.value)}
          className={`form-select w-full ${theme.bg.inactive}`}
          disabled={isSubmitting || categories.length === 0}
        >
          {categories.length === 0 ? (
            <option value="">No categories available</option>
          ) : (
            categories.map((category) => (
              <option key={category.id} value={category.id}>
                {category.name}
              </option>
            ))
          )}
        </select>
      </div>

      <div>
        <label
          htmlFor="url"
          className={`block text-sm font-medium ${theme.text.secondary} mb-1`}
        >
          URL (Optional)
        </label>
        <input
          id="url"
          type="url"
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          className={`form-input w-full ${theme.bg.inactive}`}
          placeholder="https://example.com"
          disabled={isSubmitting}
        />
      </div>

      {error && <div className="text-red-500 text-sm mt-2">{error}</div>}

      <div className="flex justify-between mt-4">
        <div>
          {event && onDelete && (
            <Button
              type="button"
              onClick={handleDelete}
              variant={ButtonVariant.DANGER}
              disabled={isSubmitting}
            >
              Delete
            </Button>
          )}
        </div>
        <div className="flex space-x-2">
          <Button
            type="button"
            onClick={onCancel}
            disabled={isSubmitting}
            className="bg-gray-200 hover:bg-gray-300 text-gray-800 dark:bg-gray-700 dark:hover:bg-gray-600 dark:text-white"
          >
            Cancel
          </Button>
          <Button type="submit" disabled={isSubmitting}>
            {isSubmitting ? "Saving..." : event ? "Update" : "Create"}
          </Button>
        </div>
      </div>

      {/* Emoji Picker */}
      {showEmojiPicker && (
        <div
          className={`absolute z-50 right-10 mt-2 ${
            emojiTarget === "title" ? "bottom-14" : "bottom-[-4rem]"
          }`}
        >
          <EmojiPicker
            height={450}
            width={350}
            onEmojiClick={(emojiData) => handleEmojiClick(emojiData)}
          />
        </div>
      )}
    </form>
  );
};

export default EventForm;
